import React, { useState, useEffect, useMemo } from 'react';
import Chart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import { getUtilizationTrend } from '@/services/api/dashboard';

interface FleetUtilizationData {
  date: number; // timestamp
  utilization: number; // percentage
  activeVehicles: number;
  trend?: number;
  operationalVehicles: number;
}

type TimeRangeType = 'Last 7 Days' | 'Last 30 Days' | 'Last 90 Days';

const FleetUtilizationChart: React.FC = () => {
  const [timeRange, setTimeRange] = useState<TimeRangeType>('Last 7 Days');
  const [chartData, setChartData] = useState<FleetUtilizationData[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [summary, setSummary] = useState({
    average: 0,
    highest: 0,
    lowest: 0
  });

  // Fetch data from API
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        const days = timeRange === 'Last 7 Days' ? 7 : timeRange === 'Last 30 Days' ? 30 : 90;
        const response = await getUtilizationTrend({ days });

        // Map API response to chart data
        const mappedData: FleetUtilizationData[] = response.data.dataPoints.map(point => {
          // Parse date and convert to local midnight timestamp
          const dateObj = new Date(point.date);
          const localMidnight = new Date(
            dateObj.getFullYear(),
            dateObj.getMonth(),
            dateObj.getDate()
          ).getTime();

          return {
            date: localMidnight,
            utilization: point.utilization,
            activeVehicles: point.activeVehicles,
            operationalVehicles: point.operationalVehicles,
            trend: point.trend
          };
        });

        setChartData(mappedData);
        setSummary(response.data.summary);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to fetch utilization data';
        setError(errorMessage);
        console.error('Failed to fetch utilization data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [timeRange]);

  // Calculate metrics based on current data
  const metrics = useMemo(() => {
    return {
      average: summary.average,
      highest: summary.highest,
      lowest: summary.lowest,
      averagePercent: summary.average,
      highestPercent: summary.highest,
      lowestPercent: summary.lowest
    };
  }, [summary]);

  // Determine X-axis label format based on time range
  const xAxisLabelFormat = useMemo(() => {
    if (timeRange === 'Last 7 Days') {
      return 'dd/M/yy'; // e.g., "13/1/25"
    } else if (timeRange === 'Last 30 Days') {
      return 'dd/M/yy'; // e.g., "13/1/25"
    } else {
      return 'dd/M/yy'; // e.g., "13/1/25"
    }
  }, [timeRange]);

  const chartOptions: ApexOptions = useMemo(() => ({
    chart: {
      type: 'line',
      height: 350,
      toolbar: {
        show: false,
      },
      zoom: {
        enabled: false,
      },
      animations: {
        enabled: true,
        easing: 'easeinout',
        speed: 800,
      },
      margin: {
        top: 10,
        bottom: 10,
        left: 0,
        right: 0,
      },
    },
    stroke: {
      curve: 'straight',
      width: 3,
      colors: ['#8B5CF6'],
    },
    grid: {
      show: true,
      borderColor: '#E8E8E8',
      strokeDashArray: 5,
      padding: {
        top: 0,
        bottom: 0,
        left: 10,
        right: 10,
      },
      xaxis: {
        lines: {
          show: true,
        },
      },
      yaxis: {
        lines: {
          show: true,
        },
      },
      row: {
        colors: undefined,
        opacity: 1,
      },
    },

    xaxis: {
      type: 'datetime',
      min: chartData.length > 0 ? chartData[0].date : undefined,
      max: chartData.length > 0 ? chartData[chartData.length - 1].date : undefined,
      labels: {
        style: {
          colors: '#A3A3A3',
          fontSize: '12px',
          fontWeight: 500,
        },
        format: xAxisLabelFormat,
        rotate: timeRange === 'Last 90 Days' ? -45 : 0,
        rotateAlways: false,
        hideOverlappingLabels: true,
        datetimeUTC: false,
      },
      tickAmount: timeRange === 'Last 7 Days' ? 6 : timeRange === 'Last 30 Days' ? 7 : 9,
      tooltip: {
        enabled: false,
      },
      axisBorder: {
        show: false,
      },
      axisTicks: {
        show: false,
      },
    },
    yaxis: {
      min: 0,
      max: 100,
      tickAmount: 4,
      labels: {
        style: {
          colors: '#A3A3A3',
          fontSize: '12px',
          fontWeight: 500,
        },
        offsetX: -10,
        formatter: function (value) {
          return Math.round(value).toString();
        },
      },
    },
    dataLabels: {
      enabled: false,
    },
    markers: {
      size: 0,
      colors: ['#8B5CF6'],
      strokeColors: '#fff',
      strokeWidth: 2,
      hover: {
        size: 6,
      },
    },
    tooltip: {
      enabled: true,
      custom: function ({ series, seriesIndex, dataPointIndex }) {
        const dataPoint = chartData[dataPointIndex];
        const utilizationValue = dataPoint.utilization;
        const activeVehiclesCount = dataPoint.activeVehicles;
        const operationalVehiclesCount = dataPoint.operationalVehicles;
        const date = new Date(dataPoint.date);
        const dayName = date.toLocaleDateString('en-US', { weekday: 'short' });
        const monthName = date.toLocaleDateString('en-US', { month: 'short' });
        const day = date.getDate();

        // Use trend from API if available
        const trend = dataPoint.trend || 0;
        const changeSign = trend >= 0 ? '+' : '-';
        const percentColor = trend >= 0 ? '#1FC16B' : '#FB3748';

        return `
          <div class="bg-white p-3 rounded-lg shadow-lg">
            <div style="color: #A3A3A3; font-size: 12px; font-weight: 500; line-height: 16px;">${dayName}, ${monthName} ${day}</div>
            <div class="flex items-center gap-2 mt-1">
              <span style="color: #171717; font-weight: 500; font-size: 18px; line-height: 24px; letter-spacing: -0.015em;">${utilizationValue.toFixed(1)}%</span>
              ${trend !== undefined ? `<span style="font-size: 12px; font-weight: 500; line-height: 16px;">
                <span style="color: #5C5C5C;">(${activeVehiclesCount} / ${operationalVehiclesCount} Active Vehicles)</span>
                <span style="color: ${percentColor};">${changeSign}${Math.abs(trend).toFixed(1)}%</span>
                <span style="color: #5C5C5C;">vs prev </span>
              </span>` : ''}
            </div>
          </div>
        `;
      },
    },
    colors: ['#8B5CF6'],
    fill: {
      type: 'solid',
    },
  }), [chartData, timeRange, xAxisLabelFormat]);

  const series = useMemo(() => [
    {
      name: 'Utilization',
      data: chartData.map(d => [d.date, d.utilization]),
    },
  ], [chartData]);

  const handleTimeRangeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setTimeRange(e.target.value as TimeRangeType);
  };


  return (
    <div className="bg-white rounded-2xl border border-gray-200 px-3 pt-4 pb-3 shadow-sm">
      {/* Header */}
      <div className="flex items-center justify-between mb-4 pb-4 border-b border-gray-200">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 flex items-center justify-center">
            <i className="ri-file-chart-line text-lg text-neutral-900" />
          </div>
          <h3 className="text-base font-medium text-gray-900" style={{ letterSpacing: '-0.011em', lineHeight: '24px' }}>Fleet Utilizations</h3>
        </div>

        <div className="relative">
          <select
            value={timeRange}
            onChange={handleTimeRangeChange}
            className="px-4 py-2.5 pr-10 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg shadow-sm focus:outline-none cursor-pointer appearance-none"
            disabled={loading}
          >
            <option value="Last 7 Days">Last 7 Days</option>
            <option value="Last 30 Days">Last 30 Days</option>
            <option value="Last 90 Days">Last 90 Days</option>
          </select>
          <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
            <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>
      </div>

      {/* Chart */}
      <div className="mb-2 fleet-utilization-chart-container">
        <style>
          {`
            .fleet-utilization-chart-container .apexcharts-gridlines-horizontal line {
              stroke: #F7F7F7 !important;
              stroke-dasharray: 0 !important;
            }
            .fleet-utilization-chart-container .apexcharts-gridlines-vertical line {
              stroke: #E8E8E8 !important;
              stroke-dasharray: 5 !important;
            }
            .fleet-utilization-chart-container .apexcharts-xaxis-label {
              font-weight: 500 !important;
            }
          `}
        </style>
        {loading ? (
          <div className="flex items-center justify-center" style={{ height: '350px' }}>
            <div className="text-gray-400">Loading...</div>
          </div>
        ) : error ? (
          <div className="flex items-center justify-center" style={{ height: '350px' }}>
            <div className="text-red-500">Error: {error}</div>
          </div>
        ) : (
          <Chart
            options={chartOptions}
            series={series}
            type="line"
            height={350}
          />
        )}
      </div>

      {/* Metrics */}
      <div className="grid grid-cols-3 gap-6 pt-4 pb-1 border-t border-gray-200">
        <div className="text-center">
          <div className="text-base font-normal text-gray-500 mb-1" style={{ letterSpacing: '-0.01em', lineHeight: '24px' }}>
            Average
          </div>
          <div className="text-[32px] font-medium" style={{ color: '#335CFF', lineHeight: '40px', letterSpacing: '-0.005em' }}>
            {metrics.averagePercent.toFixed(1)}%
          </div>
        </div>
        <div className="text-center">
          <div className="text-base font-normal text-gray-500 mb-1" style={{ letterSpacing: '-0.01em', lineHeight: '24px' }}>
            Highest
          </div>
          <div className="text-[32px] font-medium text-green-500" style={{ lineHeight: '40px', letterSpacing: '-0.005em' }}>
            {metrics.highestPercent.toFixed(1)}%
          </div>
        </div>
        <div className="text-center">
          <div className="text-base font-normal text-gray-500 mb-1" style={{ letterSpacing: '-0.01em', lineHeight: '24px' }}>
            Lowest
          </div>
          <div className="text-[32px] font-medium text-red-500" style={{ lineHeight: '40px', letterSpacing: '-0.005em' }}>
            {metrics.lowestPercent.toFixed(1)}%
          </div>
        </div>
      </div>
    </div>
  );
};

export default FleetUtilizationChart;